<script setup>
import SettingsLayout from "@/Layouts/Settings.vue";
import Input from "@/Components/Settings/Input.vue";
import Select from "@/Components/Select.vue";
import Label from "@/Components/Settings/Label.vue";
import HeaderLink from "@/Components/Settings/HeaderLink.vue";
import HeaderButton from "@/Components/Settings/HeaderButton.vue";
import BreezeAuthenticatedLayout from "@/Layouts/Authenticated.vue";
import Heading from "@/Components/Settings/Heading.vue";
import { Head, useForm } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { onUnmounted } from "vue";
import FilePreview from "@/Components/FilePreview";

import { states } from "@/shared/utils.js";

const props = defineProps({
    office: Object,
    errors: Object,
});

const canEdit = Inertia.page.props.auth.can["edit settings"];
const form = useForm({
    name: props.office.name,
    street: props.office.street,
    street_nr: props.office.street_nr,
    postal_code: props.office.postal_code,
    city: props.office.city,
    state: props.office.state,
    phone: props.office.phone,
    email: props.office.email,
    website: props.office.website,
    logo: null,
});
let saveBtnClicked = false;

function selectImage(image) {
    form.logo = image;
}

function submitForm() {
    saveBtnClicked = true;
    form.post("/office");
}

//onUnmounted(
//    Inertia.on("before", (e) => {
//        if (form.isDirty && !saveBtnClicked) {
//            return confirm("Sie haben ungespeicherte Eingaben. Wollen Sie diese Seite wirklich verlassen?");
//        }
//    })
//);
</script>
<template>
    <SettingsLayout>
        <Head title="Stammdaten - Grundeinstellungen" />

        <template #breadcrumb>Stammdaten</template>
        <template #controls>
            <HeaderLink href="/" type="close" class="mr-3"
                >Schließen</HeaderLink
            >
            <HeaderButton v-if="canEdit" type="button" @click="submitForm()"
                >Speichern</HeaderButton
            >
        </template>

        <Heading title="Stammdaten" />

        <p class="max-w-2xl text-sm text-oteoText">
            Geben Sie hier Ihre Praxis-Stammdaten ein. Wenn Sie Ihr Bundesland
            auswählen, werden automatisch die Feiertage in Ihrem Bundesland
            angezeigt. Das Logo und die Kontaktdaten sind für den Ausdruck des
            Terminzettels relevant.
        </p>

        <div class="flex mb-8">
            <div class="w-2/5">
                <div class="mt-8">
                    <Label for="name" value="Name der Praxis" />
                    <Input
                        id="name"
                        type="text"
                        class="mt-1 block w-full"
                        v-model="form.name"
                        required
                        placeholder=""
                        :disabled="!canEdit"
                    />
                    <div class="mt-2 text-red-600 text-sm" v-if="errors.name">
                        {{ errors.name }}
                    </div>
                </div>

                <div class="mt-8 flex">
                    <div class="flex-1">
                        <Label for="street" value="Straße" />
                        <Input
                            id="street"
                            type="text"
                            class="mt-1 block w-full"
                            v-model="form.street"
                            required
                            autocomplete="street-address"
                            placeholder=""
                            :disabled="!canEdit"
                        />
                    </div>

                    <div class="ml-3 w-1/5">
                        <Label for="street_nr" value="Nr." />
                        <Input
                            id="street_nr"
                            type="text"
                            class="mt-1 block w-full"
                            v-model="form.street_nr"
                            required
                            autocomplete="street-address"
                            placeholder=""
                            :disabled="!canEdit"
                        />
                    </div>
                </div>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.street">
                    {{ errors.street }}
                </div>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.street_nr">
                    {{ errors.street_nr }}
                </div>

                <div class="mt-8 flex">
                    <div class="mr-3 w-1/3">
                        <Label for="postal_code" value="PLZ" />

                        <Input
                            id="postal_code"
                            type="text"
                            class="mt-1 block w-full"
                            v-model="form.postal_code"
                            required
                            autocomplete="postal-code"
                            placeholder=""
                            :disabled="!canEdit"
                        />
                    </div>

                    <div class="flex-1">
                        <Label for="city" value="Stadt" />

                        <Input
                            id="city"
                            type="text"
                            class="mt-1 block w-full"
                            v-model="form.city"
                            required
                            autocomplete="address-level2"
                            placeholder=""
                            :disabled="!canEdit"
                        />
                    </div>
                </div>

                <div
                    class="mt-2 text-red-600 text-sm"
                    v-if="errors.postal_code"
                >
                    {{ errors.postal_code }}
                </div>

                <div class="mt-2 text-red-600 text-sm" v-if="errors.city">
                    {{ errors.city }}
                </div>

                <div class="mt-8">
                    <Label for="state" value="Bundesland" />

                    <Select
                        class="w-full"
                        id="state"
                        v-model="form.state"
                        :disabled="!canEdit"
                    >
                        <option
                            v-for="state in states"
                            :key="state.value"
                            :value="state.value"
                        >
                            {{ state.text }}
                        </option>
                    </Select>

                    <div class="mt-2 text-red-600 text-sm" v-if="errors.state">
                        {{ errors.state }}
                    </div>
                </div>

                <div class="mt-8">
                    <Label for="phone" value="Telefonnummer" />
                    <Input
                        id="phone"
                        type="text"
                        class="mt-1 block w-full"
                        v-model="form.phone"
                        required
                        placeholder=""
                        :disabled="!canEdit"
                    />
                    <div class="mt-2 text-red-600 text-sm" v-if="errors.phone">
                        {{ errors.phone }}
                    </div>
                </div>
            </div>

            <div class="w-2/5 ml-8">
                <div class="mt-8">
                    <Label for="email" value="E-Mail Adresse" />
                    <Input
                        id="email"
                        type="text"
                        class="mt-1 block w-full"
                        v-model="form.email"
                        required
                        placeholder=""
                        :disabled="!canEdit"
                    />
                    <div class="mt-2 text-red-600 text-sm" v-if="errors.email">
                        {{ errors.email }}
                    </div>
                </div>

                <div class="mt-8">
                    <Label for="website" value="Webseite" />
                    <Input
                        id="website"
                        type="text"
                        class="mt-1 block w-full"
                        v-model="form.website"
                        required
                        placeholder=""
                        :disabled="!canEdit"
                    />
                    <div
                        class="mt-2 text-red-600 text-sm"
                        v-if="errors.website"
                    >
                        {{ errors.website }}
                    </div>
                </div>

                <div class="mt-8">
                    <Label for="logo" value="Praxislogo" />

                    <FilePreview
                        @selectImage="selectImage"
                        :logo="props.office.logo_thumb"
                    ></FilePreview>

                    <div class="mt-2 text-red-600 text-sm" v-if="errors.logo">
                        {{ errors.logo }}
                    </div>
                </div>
            </div>
        </div>
    </SettingsLayout>
</template>
